import { createEffect } from 'solid-js';
import { FeatureExtractionContextType } from '@mobile-mapping/providers';
import {
  ICADObject,
  LineEditingModel,
} from '@technology/web3d-plugin-line-editing';
import {
  CAD_FEATURE_ID_KEY,
  CAD_SOURCE_FEATURE_KEY,
  FeatureCadTransformer,
  GeospatialFeature,
} from '@trimble/geospatial';
import { CadSceneProjection } from './cad-scene-projection';

export interface CreateFeatureCadMirrorParams {
  readonly model: LineEditingModel;
  readonly context: FeatureExtractionContextType;
  readonly transformer: FeatureCadTransformer;
  readonly projection: Pick<
    CadSceneProjection,
    'converterSettled' | 'converter'
  >;
}

export interface FeatureCadMirror {
  dispose(): void;
}

export function createFeatureCadMirror(
  params: CreateFeatureCadMirrorParams
): FeatureCadMirror {
  const { model, context, transformer, projection } = params;
  const mirroredById = new Map<string, { guid: string; signature: string }>();

  let disposed = false;

  const dispose = (): void => {
    if (disposed) {
      return;
    }
    disposed = true;
    mirroredById.clear();
  };

  // performAction throws on undefined — drop guids already gone from the model.
  const resolveRemovals = (...guids: string[]): ICADObject[] =>
    (model.getEntitiesByGuids(...guids) as (ICADObject | undefined)[]).filter(
      (entity): entity is ICADObject => entity !== undefined
    );

  const indexStampedEntities = (): Map<string, ICADObject> => {
    const byFeatureId = new Map<string, ICADObject>();
    for (const entity of model.allEntities()) {
      const id = entity.metaData[CAD_FEATURE_ID_KEY];
      if (typeof id === 'string' && id.length > 0) {
        byFeatureId.set(id, entity);
      }
    }
    return byFeatureId;
  };

  // Wait for a real converter — identity projection places features at raw lon/lat.
  createEffect(() => {
    const ids = context.featureIds();
    const settled = projection.converterSettled();
    if (disposed) {
      return;
    }
    if (!settled || !projection.converter()) {
      return;
    }
    const currentIds = new Set(ids);
    const stampedEntities = indexStampedEntities();
    for (const id of ids) {
      const feature = context.getFeature(id);
      if (!feature) {
        continue;
      }
      const signature = geometrySignature(feature);
      const existing = mirroredById.get(id);
      if (existing && existing.signature === signature) {
        continue;
      }

      const stamped = stampedEntities.get(id);
      if (stamped && cadSourceSignature(stamped) === signature) {
        mirroredById.set(id, { guid: stamped.guid, signature });
        continue;
      }
      const entity = transformer.toCadObjectFromFeature(feature);
      if (!entity) {
        continue;
      }
      const removals = existing ? resolveRemovals(existing.guid) : [];
      model.performAction([entity], removals, false);
      mirroredById.set(id, { guid: entity.guid ?? id, signature });
    }
    for (const id of Array.from(mirroredById.keys())) {
      if (currentIds.has(id)) {
        continue;
      }
      const record = mirroredById.get(id);
      mirroredById.delete(id);
      if (!record) {
        continue;
      }
      const removals = resolveRemovals(record.guid);
      if (removals.length > 0) {
        model.performAction([], removals, false);
      }
    }
  });

  return { dispose };
}

function geometrySignature(feature: GeospatialFeature): string {
  return JSON.stringify(feature.geometry);
}

function cadSourceSignature(entity: ICADObject): string | undefined {
  const source = entity.metaData[CAD_SOURCE_FEATURE_KEY];
  if (typeof source === 'object' && source !== null && 'geometry' in source) {
    return JSON.stringify(source.geometry);
  }
  return undefined;
}
